import React, { useEffect, useState } from 'react';
import { Stack, Row, Col, Button, Image } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import liff from '@line/liff/dist/lib';

import MainLayout from 'layout/MainLayout';
import Barcode from 'component/Barcode';
import Loader from 'component/Loader';
import SnackBar from 'component/SnackBar';
import AdBanner from 'component/AdBanner';
import AdPopup from 'component/AdPopup';
import CardPrepaidBalance from 'component/CardPrepaidBalance';
import CardPointBalance from 'component/CardPointBalance';
import { getCurrentUser, getSmartReceipt } from 'api';
import {
  EBASE_APP_URL,
  SMARTRECEIPT_LINK,
  AD_BANNERS,
  POPUP_AD_SRC,
  POPUP_GCM_SRC,
  POPUP_GCM_INFO
} from 'lib/constants';

import CardBg from 'assets/image/card_bg.png';
import ebaseBannerImg from 'assets/image/ebase_banner_img.png';
import { openWindow } from 'lib';
import smartReceiptOn from 'assets/image/smart_receipt_on.png';
import smartReceiptOff from 'assets/image/smart_receipt_off.png';
import { UserDataType } from 'types/UserType';

const Card: React.FC<{ cardNumber: string }> = ({ cardNumber }) => {
  return (
    <Stack className="p-3 full-width card bg-transparent border-0">
      <Stack className="section-cont bg-transparent border-0 section-cont--rounded-2 position-relative">
        <Image src={CardBg} className="mw-100" />
        <div className="text-center position-absolute card__barcode">
          <Barcode value={cardNumber} label={cardNumber} showLabel={false} />
        </div>
      </Stack>
    </Stack>
  );
};

const SectionCard: React.FC<
  React.PropsWithChildren<React.ComponentProps<typeof Stack>>
> = ({ children, ...props }) => {
  return (
    <Stack
      {...props}
      className="px-3 py-4 px-4 py-3 mb-3 section-cont section-cont--rounded-2 section-cont--shadow justify-content-between"
    >
      {children}
    </Stack>
  );
};

const CardHome: React.FC = () => {
  const navigate = useNavigate();
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [userData, setUserData] = useState<UserDataType>();
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isSmartReceiptLinked, setIsSmartReceiptLinked] = useState<
    'linked' | 'unlinked' | 'error'
  >('unlinked');
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchUserData = () => {
    if (searchParams.get('smartConnect') === 'true') {
      setShowSnackBar(true);
      setSearchParams('');
    }

    const user = getCurrentUser().then((res) => {
      setUserData(res.data);
    });

    const smartReceipt = getSmartReceipt().then((res) => {
      if (res && res.status === 'succeeded') {
        setIsSmartReceiptLinked(res.data.linked ? 'linked' : 'unlinked');
      } else {
        setIsSmartReceiptLinked('error');
      }
    });

    Promise.allSettled([user, smartReceipt]).then(() =>
      setTimeout(() => setIsFinished(true), 300)
    );
  };

  // 初期データ取得
  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <MainLayout className="home" footer>
      {isFinished ? (
        <Row>
          <Col md={7} className="mx-auto">
            <Card cardNumber={userData?.card_no || ''} />
            <CardPrepaidBalance />
            <CardPointBalance />

            {/* 広告バナー */}
            {AD_BANNERS.map((banner, index) => (
              <Stack
                key={index}
                direction="horizontal"
                className="justify-content-center mb-3"
              >
                <AdBanner
                  fluctGroupId={banner.fluctGroupId}
                  fluctUnitName={banner.fluctUnitName}
                  fluctUnitId={banner.fluctUnitId}
                />
              </Stack>
            ))}

            {/* 広告ポップアップ */}
            <AdPopup
              adSrc={POPUP_AD_SRC}
              gcmSrc={POPUP_GCM_SRC}
              gcmInfo={POPUP_GCM_INFO}
              cardNo={userData?.card_no || ''}
            />

            {/* スマートレシート */}
            {isSmartReceiptLinked !== 'error' ? (
              <Button className="d-block w-100 p-0 mb-3 border-0">
                <Image
                  {...(isSmartReceiptLinked === 'linked'
                    ? {
                        src: smartReceiptOn,
                        alt: 'スマートレシート連携済',
                        onClick: () => openWindow(SMARTRECEIPT_LINK)
                      }
                    : {
                        src: smartReceiptOff,
                        alt: 'スマートレシート未連携',
                        onClick: () => navigate('/smart-receipt/notice')
                      })}
                  fluid
                  className="w-100"
                />
              </Button>
            ) : (
              <SectionCard>
                <span>
                  現在スマートレシート連携サービスがご利用いただけません。しばらくしてから、もう一度ご利用をお試しください。
                </span>
                <span>他の機能は通常通りご利用いただけます。</span>
              </SectionCard>
            )}

            {/* eBASE バナー */}
            {/* FIXME: onClickで逃げているので、reactのイベントフック周りでどうにかしたいかもしれない */}
            <SectionCard
              as="a"
              href="javascript:void(0)"
              onClick={() =>
                liff.openWindow({ url: EBASE_APP_URL, external: true })
              }
            >
              <Image src={ebaseBannerImg} alt="eBASE バナー画像" fluid />
            </SectionCard>
          </Col>
        </Row>
      ) : (
        <Row className="mt-4">
          <Col md={7} className="mx-auto">
            <Loader />
          </Col>
        </Row>
      )}
      {/* スマレシ連携完了用のトースト */}
      <SnackBar
        message="スマートレシートと連携しました。"
        show={showSnackBar}
      />
    </MainLayout>
  );
};
export default CardHome;
